import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Subject, finalize, takeUntil} from 'rxjs';
import {FuseValidators} from '@fuse/validators/validators';
import {fuseAnimations} from '@fuse/animations';
import {FuseAlertType} from '@fuse/components/alert';
import {FuseConfirmationService} from '@fuse/services/confirmation';
import {FuseConfirmationDialogComponent} from '@fuse/services/confirmation/dialog/dialog.component';
import {AccountRole} from '@enums/AccountRole';
import {User} from '@models/user.interface';
import {UserService} from '@core/user/user.service';
import {FianzaEstatus, FianzaEstatusRequest, FianzaResponse} from '@models/fianza.model';
import {FianzaService} from '@services/fianza.service';
import {DiscordService} from '@services/discord.service';
import {gettext} from '@utils/util-builder';
import moment from 'moment';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
    selector: 'app-fianzas',
    templateUrl: './fianzas.component.html',
    styleUrls: ['./fianzas.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FianzasComponent implements OnInit, OnChanges, OnDestroy {

    @Input() contractUuid: string;
    @Input() providerName?: string;   // razón social del proveedor (para notificación Discord)
    @Input() companyName?: string;    // nombre de la compañía/contratante (para notificación Discord)
    @Input() contractTag?: string;    // folio del contrato (para notificación Discord)
    @ViewChild('uploadFianzaDialogTemplate') uploadFianzaDialogTemplate: TemplateRef<any>;
    @ViewChild('updateEstatusDialogTemplate') updateEstatusDialogTemplate: TemplateRef<any>;

    fianzas: FianzaResponse[] = [];
    displayedColumns: string[] = ['numeroPoliza', 'companiaAfianzadora', 'monto', 'fechaInicioVigencia', 'fechaFinVigencia', 'pagada', 'estatus', 'acciones'];
    user: User;
    uploadForm: UntypedFormGroup;
    updateEstatusForm: UntypedFormGroup;
    isSubmitting: boolean = false;
    isExtractingPdf: boolean = false;
    camposAutocompletados: number = 0;
    selectedFianza: FianzaResponse | null = null;
    esEdicionMaster: boolean = false;
    fianzaEnEdicion: FianzaResponse | null = null;
    readonly accountRole: typeof AccountRole = AccountRole;
    readonly minDate: Date = new Date(2000, 0, 1);
    readonly maxDate: Date = new Date(2099, 0, 1);

    alertCargar: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertEstatus: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertEliminar: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertDescargar: { type: FuseAlertType; message: string } = {type: 'info', message: ''};

    private readonly _maxSizeFile: number = 10485760;
    private readonly _alertTime: number = 5000;
    private _showAlertCargar: boolean = false;
    private _showAlertEstatus: boolean = false;
    private _showAlertEliminar: boolean = false;
    private _showAlertDescargar: boolean = false;
    private _uploadDialogRef: MatDialogRef<any> | null = null;
    private _updateEstatusDialogRef: MatDialogRef<any> | null = null;
    private readonly _unsubscribeAll$: Subject<any> = new Subject<any>();

    constructor(
        private readonly _cdr: ChangeDetectorRef,
        private readonly _fb: UntypedFormBuilder,
        private readonly _fuseConfirmationService: FuseConfirmationService,
        public readonly dialog: MatDialog,
        private readonly _userService: UserService,
        private readonly _fianzaService: FianzaService,
        private readonly _discordService: DiscordService
    ) {
    }

    get showAlertCargar(): boolean {
        return this._showAlertCargar;
    }

    set showAlertCargar(val: boolean) {
        this._showAlertCargar = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertCargar = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    get showAlertEstatus(): boolean {
        return this._showAlertEstatus;
    }

    set showAlertEstatus(val: boolean) {
        this._showAlertEstatus = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertEstatus = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    get showAlertEliminar(): boolean {
        return this._showAlertEliminar;
    }

    set showAlertEliminar(val: boolean) {
        this._showAlertEliminar = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertEliminar = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    get showAlertDescargar(): boolean {
        return this._showAlertDescargar;
    }

    set showAlertDescargar(val: boolean) {
        this._showAlertDescargar = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout((): void => {
                this._showAlertDescargar = false;
                this._cdr.markForCheck();
            }, this._alertTime);
        }
    }

    get isDateRangeInvalid(): boolean {
        return !!this.uploadForm &&
            this.uploadForm.hasError('dateRange') &&
            (this.uploadForm.get('fechaInicioVigencia')?.touched || this.uploadForm.get('fechaFinVigencia')?.touched);
    }

    get showMotivoRechazo(): boolean {
        return this.updateEstatusForm?.get('estatus')?.value === 'RECHAZADA';
    }

    get mostrarAvisoIncompleto(): boolean {
        return !this.isExtractingPdf
            && !!this.uploadForm?.get('file')?.value
            && this.camposAutocompletados < 5;
    }

    ngOnInit(): void {
        pdfjsLib.GlobalWorkerOptions.workerSrc =
            '//cdn.jsdelivr.net/npm/pdfjs-dist@3.7.107/build/pdf.worker.js';

        this._userService.user$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((user: User): void => {
            this.user = user;
        });
        this._buildForms();
        this._suscribirCambioArchivo();
        this.listarFianzas();
    }

    ngOnChanges(): void {
        this.listarFianzas();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll$.next(null);
        this._unsubscribeAll$.complete();
    }

    openUploadDialog(): void {
        this.esEdicionMaster = false;
        this.fianzaEnEdicion = null;
        this._resetUploadForm();
        this.uploadForm.get('file')!.setValidators([Validators.required, FuseValidators.maxContentSize(this._maxSizeFile)]);
        this.uploadForm.get('file')!.updateValueAndValidity();
        this.camposAutocompletados = 0;
        this._uploadDialogRef = this.dialog.open(this.uploadFianzaDialogTemplate, {
            width: '50%',
            panelClass: 'ghost-white-dialog'
        });
    }

    /** Edición completa por MASTER (AC-13): precarga el formulario, el archivo es opcional. */
    openEditDialog(fianza: FianzaResponse): void {
        this.esEdicionMaster = true;
        this.fianzaEnEdicion = fianza;
        this.uploadForm.patchValue({
            numeroPoliza: fianza.numeroPoliza,
            companiaAfianzadora: fianza.companiaAfianzadora,
            objeto: fianza.objeto,
            monto: fianza.monto,
            fechaInicioVigencia: moment(fianza.fechaInicioVigencia).toDate(),
            fechaFinVigencia: moment(fianza.fechaFinVigencia).toDate(),
            pagada: fianza.pagada,
            file: undefined
        });
        this.uploadForm.get('file')!.clearValidators();
        this.uploadForm.get('file')!.updateValueAndValidity();
        this.camposAutocompletados = 5;
        this._uploadDialogRef = this.dialog.open(this.uploadFianzaDialogTemplate, {
            width: '50%',
            panelClass: 'ghost-white-dialog'
        });
    }

    /** Visible para proveedor (sin cambios) y también para MASTER, en representación del proveedor (AC-10). */
    canUpload(): boolean {
        return this.user?.rol === AccountRole.provider || this.user?.rol === AccountRole.master;
    }

    /** Solo MASTER puede editar cualquier campo de cualquier fianza (AC-13). */
    canEditAsMaster(): boolean {
        return this.user?.rol === AccountRole.master;
    }

    openUpdateEstatusDialog(fianza: FianzaResponse): void {
        this.selectedFianza = fianza;
        this.updateEstatusForm.patchValue({estatus: '', motivoRechazo: ''});
        this.updateEstatusForm.markAsUntouched();
        this._updateEstatusDialogRef = this.dialog.open(this.updateEstatusDialogTemplate, {
            width: '40%',
            panelClass: 'ghost-white-dialog'
        });
    }

    uploadFianza(): void {
        this.showAlertCargar = false;
        if (this.uploadForm.invalid) return;
        if (this.esEdicionMaster) {
            this._guardarEdicionMaster();
            return;
        }
        this.isSubmitting = true;

        const formValue: any = this.uploadForm.value;
        const file: File = formValue.file;

        if (!file) {
            this.alertCargar = {type: 'error', message: 'Por favor, seleccione un archivo PDF válido.'};
            this.showAlertCargar = true;
            this.isSubmitting = false;
            return;
        }

        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        formData.append('data', new Blob([JSON.stringify({
            numeroPoliza: formValue.numeroPoliza,
            companiaAfianzadora: formValue.companiaAfianzadora,
            objeto: formValue.objeto,
            monto: formValue.monto,
            fechaInicioVigencia: moment(formValue.fechaInicioVigencia).format('YYYY-MM-DD'),
            fechaFinVigencia: moment(formValue.fechaFinVigencia).format('YYYY-MM-DD'),
            pagada: formValue.pagada
        })], {type: 'application/json'}));

        this._fianzaService.cargar(this.contractUuid, formData).pipe(
            finalize((): void => {
                this.isSubmitting = false;
                this._uploadDialogRef?.close();
                this.showAlertCargar = true;
            })
        ).subscribe({
            next: (): void => {
                this.alertCargar = {type: 'success', message: 'Fianza cargada correctamente.'};
                // Notificación a Discord como último paso del alta (tras validar → S3 → BD).
                // AC-8: distingue si la acción la ejecutó MASTER en representación del proveedor.
                const esMaster: boolean = this.user?.rol === AccountRole.master;
                this._discordService.sendPolizasFianzasChannelMessage({
                    username: esMaster ? 'ALTA FIANZA (MASTER)' : 'ALTA FIANZA',
                    content: esMaster
                        ? `MASTER cargó la Fianza ${formValue.numeroPoliza} en representación del proveedor ${this.providerName} para el contrato ${this.contractTag} para la compañia ${this.companyName}`
                        : `El proveedor ${this.providerName} ha cargado la Fianza ${formValue.numeroPoliza} para el contrato ${this.contractTag} para la compañia ${this.companyName}`
                });
                this.listarFianzas();
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error uploading fianza:', err);
                const serverMessage: string = err.error?.message || err.error?.error?.error || err.error?.error;
                const message: string = err.status === 409 && serverMessage
                    ? serverMessage
                    : 'Ocurrió un error al cargar la fianza.';
                this.alertCargar = {type: 'error', message};
            }
        });
    }

    /** Edición completa por MASTER (AC-13, AC-14): reemplazo de archivo opcional. */
    private _guardarEdicionMaster(): void {
        this.isSubmitting = true;
        const formValue: any = this.uploadForm.value;
        const file: File | undefined = formValue.file;

        const formData: FormData = new FormData();
        if (file) {
            formData.append('file', file, file.name);
        }
        formData.append('data', new Blob([JSON.stringify({
            numeroPoliza: formValue.numeroPoliza,
            companiaAfianzadora: formValue.companiaAfianzadora,
            objeto: formValue.objeto,
            monto: formValue.monto,
            fechaInicioVigencia: moment(formValue.fechaInicioVigencia).format('YYYY-MM-DD'),
            fechaFinVigencia: moment(formValue.fechaFinVigencia).format('YYYY-MM-DD'),
            pagada: formValue.pagada
        })], {type: 'application/json'}));

        const uuid: string = this.fianzaEnEdicion!.uuid;
        this._fianzaService.updateAsMaster(uuid, formData).pipe(
            finalize((): void => {
                this.isSubmitting = false;
                this._uploadDialogRef?.close();
                this.showAlertCargar = true;
                this.esEdicionMaster = false;
                this.fianzaEnEdicion = null;
            })
        ).subscribe({
            next: (fianza: FianzaResponse): void => {
                this.alertCargar = {type: 'success', message: 'Fianza actualizada correctamente.'};
                // AC-8: la edición de MASTER siempre indica que actuó en representación del proveedor.
                this._discordService.sendPolizasFianzasChannelMessage({
                    username: 'EDICIÓN FIANZA (MASTER)',
                    content: `MASTER editó la Fianza ${fianza.numeroPoliza} en representación del proveedor ${this.providerName} para el contrato ${this.contractTag}`
                });
                this.listarFianzas();
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error updating fianza:', err);
                this.alertCargar = {type: 'error', message: 'Ocurrió un error al actualizar la fianza.'};
            }
        });
    }

    actualizarEstatus(): void {
        this.showAlertEstatus = false;
        if (this.updateEstatusForm.invalid || !this.selectedFianza) return;
        this.isSubmitting = true;

        const body: FianzaEstatusRequest = this.updateEstatusForm.value;
        this._fianzaService.actualizarEstatus(this.selectedFianza.uuid, body).pipe(
            finalize((): void => {
                this.isSubmitting = false;
                this._updateEstatusDialogRef?.close();
                this.showAlertEstatus = true;
            })
        ).subscribe({
            next: (): void => {
                this.alertEstatus = {type: 'success', message: 'Estatus actualizado correctamente.'};
                this.listarFianzas();
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error updating fianza status:', err.message);
                this.alertEstatus = {type: 'error', message: 'Ocurrió un error al actualizar el estatus.'};
            }
        });
    }

    descargarPdf(uuid: string): void {
        this.showAlertDescargar = false;
        this._fianzaService.descargarArchivo(uuid).pipe(
            finalize((): void => {
                this.showAlertDescargar = true;
            })
        ).subscribe({
            next: (blob: Blob): void => {
                const url: string = window.URL.createObjectURL(blob);
                const a: HTMLAnchorElement = document.createElement('a');
                a.href = url;
                a.download = `fianza-${uuid}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
                this.alertDescargar = {type: 'success', message: 'PDF descargado correctamente.'};
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error downloading fianza PDF:', err.message);
                this.alertDescargar = {type: 'error', message: 'Ocurrió un error al descargar el PDF.'};
            }
        });
    }

    eliminarFianza(fianza: FianzaResponse): void {
        const confirmation: MatDialogRef<FuseConfirmationDialogComponent> = this._fuseConfirmationService.open({
            title: 'Eliminar fianza',
            message: `¿Está seguro de que desea eliminar la fianza con póliza ${fianza.numeroPoliza}? ¡Esta acción no se puede deshacer!`,
            actions: {confirm: {label: 'Aceptar'}, cancel: {label: 'Cancelar'}}
        });

        confirmation.afterClosed().subscribe((result: string): void => {
            if (result === 'confirmed') {
                this._fianzaService.eliminar(fianza.uuid).pipe(
                    finalize((): void => {
                        this.showAlertEliminar = true;
                    })
                ).subscribe({
                    next: (): void => {
                        this.alertEliminar = {
                            type: 'success',
                            message: `La fianza con póliza ${fianza.numeroPoliza} se ha eliminado correctamente.`
                        };
                        this.listarFianzas();
                    },
                    error: (err: HttpErrorResponse): void => {
                        console.error('Error deleting fianza:', err.message);
                        this.alertEliminar = {type: 'error', message: 'Ocurrió un error al eliminar la fianza.'};
                    }
                });
            }
        });
    }

    canProviderDelete(fianza: FianzaResponse): boolean {
        return this.user?.rol === AccountRole.provider && fianza.estatus === 'EN_REVISION';
    }

    canUpdateEstatus(fianza: FianzaResponse): boolean {
        return this.user?.rol === AccountRole.master && fianza.estatus === 'EN_REVISION';
    }

    getEstatusClass(estatus: FianzaEstatus): string {
        switch (estatus) {
            case 'EN_REVISION': return 'bg-amber-100 text-amber-800';
            case 'ACEPTADA_VIGENTE': return 'bg-green-100 text-green-800';
            case 'VENCIDA': return 'bg-red-100 text-red-800';
            case 'RECHAZADA': return 'bg-gray-200 text-gray-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    }

    getEstatusLabel(estatus: FianzaEstatus): string {
        switch (estatus) {
            case 'EN_REVISION': return 'En Revisión';
            case 'ACEPTADA_VIGENTE': return 'Aceptada / Vigente';
            case 'VENCIDA': return 'Vencida';
            case 'RECHAZADA': return 'Rechazada';
            default: return estatus;
        }
    }

    listarFianzas(): void {
        if (!this.contractUuid) return;
        this._fianzaService.listarPorContrato(this.contractUuid).subscribe({
            next: (fianzas: FianzaResponse[]): void => {
                this.fianzas = fianzas;
                this._cdr.markForCheck();
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error fetching fianzas:', err.message);
            }
        });
    }

    private _buildForms(): void {
        this.uploadForm = this._fb.group({
            numeroPoliza: ['', [Validators.required]],
            companiaAfianzadora: ['', [Validators.required]],
            objeto: ['', [Validators.required]],
            monto: ['', [Validators.required, Validators.min(0.01)]],
            fechaInicioVigencia: ['', [Validators.required]],
            fechaFinVigencia: ['', [Validators.required]],
            pagada: [false],
            file: [undefined, [Validators.required, FuseValidators.maxContentSize(this._maxSizeFile)]]
        }, {
            validators: [this._dateRangeValidator]
        });

        this.updateEstatusForm = this._fb.group({
            estatus: ['', [Validators.required]],
            motivoRechazo: ['']
        });

        this.updateEstatusForm.get('estatus')?.valueChanges
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe((value: FianzaEstatus): void => {
                const motivoControl = this.updateEstatusForm.get('motivoRechazo');
                if (value === 'RECHAZADA') {
                    motivoControl?.setValidators([Validators.required]);
                } else {
                    motivoControl?.clearValidators();
                }
                motivoControl?.updateValueAndValidity();
            });
    }

    private _resetUploadForm(): void {
        this.uploadForm.reset({
            numeroPoliza: '',
            companiaAfianzadora: '',
            objeto: '',
            monto: '',
            fechaInicioVigencia: '',
            fechaFinVigencia: '',
            pagada: false,
            file: undefined
        });
    }

    private _suscribirCambioArchivo(): void {
        this.uploadForm.get('file')?.valueChanges
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe((file: File): void => {
                if (file) {
                    this._extraerDatosPdf(file);
                }
            });
    }

    private _extraerDatosPdf(file: File): void {
        this.isExtractingPdf = true;
        this.camposAutocompletados = 0;
        this._cdr.markForCheck();

        const reader: FileReader = new FileReader();
        reader.onload = async (event: ProgressEvent<FileReader>): Promise<void> => {
            try {
                const texto: string = await gettext(event.target?.result);
                const datos = this._parsearTextoPdf(texto);
                this._rellenarFormulario(datos);
            } catch (err) {
                console.error('Error al extraer datos del PDF de fianza:', err);
            } finally {
                this.isExtractingPdf = false;
                this._cdr.markForCheck();
            }
        };
        reader.readAsArrayBuffer(file);
    }

    private _parsearTextoPdf(texto: string): {
        numeroPoliza?: string;
        companiaAfianzadora?: string;
        monto?: number;
        fechaInicioVigencia?: moment.Moment;
        fechaFinVigencia?: moment.Moment;
    } {
        const datos: {
            numeroPoliza?: string;
            companiaAfianzadora?: string;
            monto?: number;
            fechaInicioVigencia?: moment.Moment;
            fechaFinVigencia?: moment.Moment;
        } = {};

        // Número de póliza
        // Patrón 1: "FIANZA: 0032500000380" (encabezado MAPFRE y similares)
        const mPolizaFianza = texto.match(/\bFIANZA\s*:\s*([\d\-\/\.]+)/i);
        // Patrón 2: "PÓLIZA N°", "NÚMERO DE PÓLIZA", etc.
        const mPolizaTrad = texto.match(
            /(?:p[oó]liza[\s:]*n[uú]?\.?[\s:#]*|n[uú]mero\s+de\s+p[oó]liza[\s:#]*|no\.?\s*(?:de\s+)?p[oó]liza[\s:#]*)([\w\-\/\.]+)/i
        );
        const poliza = mPolizaFianza?.[1]?.trim() || mPolizaTrad?.[1]?.trim();
        if (poliza) { datos.numeroPoliza = poliza; }

        // Compañía afianzadora
        // Patrón 1: nombre institucional antes de "EN EL EJERCICIO" o "SE CONSTITUYE FIADORA"
        // Usa [A-Za-z] como inicio para que no cruce barreras de dígitos (ej: "26,663.86MAPFRE")
        const mCompaniaInst = texto.match(
            /([A-Za-z][A-Za-z\s\.,]+?(?:FIANZAS?|AFIANZADORA?|SEGUROS?)(?:\s*,?\s*S\.?\s*A\.?(?:\s+DE\s+C\.?\s*V\.?)?)?)\s*(?:,?\s*EN\s+EL\s+EJERCICIO|,?\s*SE\s+CONSTITUYE\s+FIADORA)/i
        );
        // Patrón 2: etiqueta "afianzadora: Nombre"
        const mCompaniaLabel = texto.match(
            /(?:compa[nñ][ií]a\s+afianzadora|afianzadora|fianzadora|aseguradora)[\s:]*([\S][^\n\r,]{3,79}?)(?=\s*(?:\r?\n|S\.A|SA\s|,))/i
        );
        const compania = mCompaniaInst?.[1]?.trim() || mCompaniaLabel?.[1]?.trim();
        if (compania) { datos.companiaAfianzadora = compania; }

        // Monto
        // Patrón 1: "MONTO:...1,825,743.45" — pdfjs puede intercalar otras etiquetas entre
        // "MONTO:" y el valor numérico (ej: "MONTO:MOVIMIENTO: 1,825,743.45")
        const mMontoLabel = texto.match(/MONTO:[\s\S]{0,50}?([\d]{1,3}(?:,[\d]{3})*\.[\d]{2})/i);
        // Patrón 2: patrones genéricos de respaldo
        const mMontoGeneral = texto.match(
            /(?:importe(?:\s+de\s+la\s+(?:fianza|p[oó]liza))?|suma\s+(?:afianzada|asegurada))[\s:$]*([\d,]+\.?\d{0,2})/i
        );
        const montoStr = mMontoLabel?.[1] || mMontoGeneral?.[1];
        if (montoStr) {
            const n: number = parseFloat(montoStr.replace(/,/g, ''));
            if (!isNaN(n) && n > 0) { datos.monto = n; }
        }

        // Vigencia
        // Patrón 1: "VIGENCIA DEL: 05/02/2025 04/02/2026" — pdfjs extrae ambas fechas
        // consecutivas sin "AL:" entre ellas (el "AL:" aparece después en el stream)
        const mVigConsecutivo = texto.match(
            /VIGENCIA DEL:\s*([\d]{1,2}\/[\d]{1,2}\/[\d]{2,4})\s+([\d]{1,2}\/[\d]{1,2}\/[\d]{2,4})/i
        );
        if (mVigConsecutivo) {
            const ini = this._parsearFecha(mVigConsecutivo[1]);
            const fin = this._parsearFecha(mVigConsecutivo[2]);
            if (ini?.isValid()) { datos.fechaInicioVigencia = ini; }
            if (fin?.isValid()) { datos.fechaFinVigencia = fin; }
        }

        // Patrón 2: "VIGENCIA DEL: fecha AL: fecha" (con "AL:" explícito)
        if (!datos.fechaInicioVigencia || !datos.fechaFinVigencia) {
            const mVigConAl = texto.match(
                /VIGENCIA\s+DEL?\s*:?\s*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})\s*AL?\s*:?\s*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})/i
            );
            if (mVigConAl) {
                if (!datos.fechaInicioVigencia) {
                    const ini = this._parsearFecha(mVigConAl[1]);
                    if (ini?.isValid()) { datos.fechaInicioVigencia = ini; }
                }
                if (!datos.fechaFinVigencia) {
                    const fin = this._parsearFecha(mVigConAl[2]);
                    if (fin?.isValid()) { datos.fechaFinVigencia = fin; }
                }
            }
        }

        // Patrón 3: "DEL fecha AL fecha"
        if (!datos.fechaInicioVigencia || !datos.fechaFinVigencia) {
            const mVigJunta = texto.match(
                /del?\s*:?\s*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})\s*al?\s*:?\s*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})/i
            );
            if (mVigJunta) {
                if (!datos.fechaInicioVigencia) {
                    const ini = this._parsearFecha(mVigJunta[1]);
                    if (ini?.isValid()) { datos.fechaInicioVigencia = ini; }
                }
                if (!datos.fechaFinVigencia) {
                    const fin = this._parsearFecha(mVigJunta[2]);
                    if (fin?.isValid()) { datos.fechaFinVigencia = fin; }
                }
            }
        }

        // Patrón individual para fecha inicio
        if (!datos.fechaInicioVigencia) {
            const mIni = texto.match(
                /(?:inicio(?:\s+de\s+vigencia)?|vigente\s+desde|fecha\s+de\s+inicio|vigencia\s+(?:desde|del))[\s:,]*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})/i
            );
            if (mIni?.[1]) {
                const d = this._parsearFecha(mIni[1]);
                if (d?.isValid()) { datos.fechaInicioVigencia = d; }
            }
        }

        // Patrón individual para fecha fin
        if (!datos.fechaFinVigencia) {
            const mFin = texto.match(
                /(?:vencimiento|fin\s+de\s+vigencia|vigente\s+hasta|fecha\s+de\s+vencimiento|expira(?:ci[oó]n)?)[\s:,]*([\d]{1,2}[\/\-][\d]{1,2}[\/\-][\d]{2,4})/i
            );
            if (mFin?.[1]) {
                const d = this._parsearFecha(mFin[1]);
                if (d?.isValid()) { datos.fechaFinVigencia = d; }
            }
        }

        return datos;
    }

    private _parsearFecha(fechaStr: string): moment.Moment | null {
        if (!fechaStr) { return null; }
        const m = moment(fechaStr.trim(), ['DD/MM/YYYY', 'DD-MM-YYYY', 'D/M/YYYY', 'D-M-YYYY', 'YYYY-MM-DD'], true);
        return m.isValid() ? m : null;
    }

    private _rellenarFormulario(datos: {
        numeroPoliza?: string;
        companiaAfianzadora?: string;
        monto?: number;
        fechaInicioVigencia?: moment.Moment;
        fechaFinVigencia?: moment.Moment;
    }): void {
        let count: number = 0;
        const patch: Record<string, unknown> = {};

        if (datos.numeroPoliza)        { patch['numeroPoliza']        = datos.numeroPoliza;        count++; }
        if (datos.companiaAfianzadora) { patch['companiaAfianzadora'] = datos.companiaAfianzadora; count++; }
        if (datos.monto)               { patch['monto']               = datos.monto;               count++; }
        if (datos.fechaInicioVigencia) { patch['fechaInicioVigencia'] = datos.fechaInicioVigencia; count++; }
        if (datos.fechaFinVigencia)    { patch['fechaFinVigencia']    = datos.fechaFinVigencia;    count++; }

        if (count > 0) {
            this.uploadForm.patchValue(patch);
        }
        this.camposAutocompletados = count;
    }

    private readonly _dateRangeValidator = (group: AbstractControl): ValidationErrors | null => {
        const inicio = group.get('fechaInicioVigencia')?.value;
        const fin = group.get('fechaFinVigencia')?.value;
        if (inicio && fin && new Date(fin) <= new Date(inicio)) {
            return {dateRange: true};
        }
        return null;
    };
}
